import React, { ReactNode } from "react";

import { IDocument } from "../../services/fetchPrismic.service";

import { bannerCreator } from "./components/bannersCreator";
import BannersElement from "./components/BannersElement/BannersElement";
import { usePrismicData } from "./hooks/usePrismicData";
import {
  IBanner,
  IBannerItem,
  IData,
  IPrismic,
  IPrismicData,
  ITemplateName,
  ITemplateType,
} from "./types";

const templates = [
  "homepage",
  "orderConfirmation",
  "preheader",
  "underMenu",
  "background",
  "product",
  "category",
];

const getBannersElements = (
  prismicData: IDocument<IPrismic>,
  templateType: ITemplateType,
  templateName: ITemplateName | null,
  pageType: string,
  { brandId, productId, groupId, segmentIds, productSku }: IData = {}
): IBanner[] => {
  const { data = null } = prismicData || {};
  const bodyIndex = templates.indexOf(templateType);
  const currentBody = data && data[`body${bodyIndex > 0 ? bodyIndex : ""}`];

  if (bodyIndex < 0 || !data || !currentBody || !bannerCreator[templateType]) {
    return [];
  }

  const options = {
    brandId,
    data,
    groupId,
    pageType,
    productId,
    productSku,
    segmentIds,
    templateName,
    templateType,
  };

  return bannerCreator[templateType](currentBody, options);
};

export interface IProps {
  children?: ReactNode;
  className?: string;
  template: string;
  pageType?: string;
  brandId?: string;
  productId?: string;
  groupId?: number;
  segmentIds?: number[];
  productSku?: string;
  setBanner?: (bannerItem: IBannerItem) => void;
  bannerData?: IPrismicData;
}

export const Banner = (props: IProps): JSX.Element | null => {
  const {
    children,
    template,
    pageType,
    groupId,
    brandId,
    productId,
    segmentIds,
    productSku,
    className,
    setBanner,
    bannerData,
  } = props;

  const [templateType, templateName = null] = template.split(":") as [
    ITemplateType,
    ITemplateName | undefined
  ];

  const prismicData = usePrismicData({ bannerData });

  const active: ITemplateType[] = [
    "homepage",
    "orderConfirmation",
    "preheader",
    "underMenu",
    "background",
    "product",
    "category",
  ];

  if (!active.includes(templateType) || !pageType || !prismicData) {
    return children ? <>{children}</> : null;
  }

  // console.log("#>", template, "\t", pageType, brandId, productId, productSku);

  const bannersBlock = getBannersElements(
    prismicData,
    templateType,
    templateName,
    pageType,
    { brandId, productId, groupId, segmentIds, productSku }
  );

  const bannerBlockIndex =
    templateType === "homepage" && templateName && templateName === "selections"
      ? 1
      : 0;

  if (!bannersBlock || bannersBlock.length === 0) {
    return children ? <>{children}</> : null;
  }

  /**
   * Share banner data wth parent
   */
  if (setBanner && templateName) {
    const obj = bannersBlock.map((banner) => banner)[0];

    setBanner({ id: templateName, banner: obj });
  }

  return (
    <BannersElement
      className={className}
      bannersBlock={bannersBlock}
      templateType={templateType}
      templateName={templateName}
      pageType={pageType}
      bannerBlockIndex={bannerBlockIndex}
      brandId={brandId}
    >
      {children}
    </BannersElement>
  );
};
